<script setup lang="ts">
/**
 * PageSettingsRuleList — tekstreglerne "felt · operator · værdi" i ét
 * regelsæt ([D-293]) eller i en enkel regelliste ([D-476], fx Kampagners
 * "Varer der aldrig får kampagnepris"). Kompakt nok til drawerens 300 px:
 * felt + operator på én linje, værdi + fjern-knap på den næste.
 *
 * Kladden lever hos forælderen; her udsendes hele den nye liste ved hver
 * ændring. En regel uden værdi vises med et hint, fordi serveren dropper den
 * tavst ved gem (sanitizePageSettingRules) — brugeren skal se det FØR Gem.
 */

import {
  PAGE_SETTING_RULE_OPS,
  PAGE_SETTING_RULE_OP_LABELS,
  PAGE_SETTING_RULE_VALUE_MAX,
  PAGE_SETTING_RULES_MAX,
  type PageSettingOption,
  type PageSettingRule,
  type PageSettingRuleOp,
} from '@cockpit/shared';

import { ICONS } from '../icons';

const props = defineProps<{
  rules: PageSettingRule[];
  ruleFields: readonly PageSettingOption[];
  disabled: boolean;
  /** Forstavelse til skærmlæser-navnene, fx "Regelsæt 1, " (tom for en enkel liste). */
  labelPrefix: string;
}>();

const emit = defineEmits<{
  (e: 'update', rules: PageSettingRule[]): void;
}>();

function addRule(): void {
  if (props.rules.length >= PAGE_SETTING_RULES_MAX) return;
  const field = props.ruleFields[0]?.value ?? '';
  emit('update', [...props.rules, { field, op: 'contains', value: '' }]);
}
function updateRule(ri: number, patch: Partial<PageSettingRule>): void {
  emit('update', props.rules.map((r, i) => (i === ri ? { ...r, ...patch } : r)));
}
function removeRule(ri: number): void {
  emit('update', props.rules.filter((_, i) => i !== ri));
}
const selectValue = (ev: Event): string => (ev.target as HTMLSelectElement).value;
const inputValue = (ev: Event): string => (ev.target as HTMLInputElement).value;
const RULE_OPS: readonly PageSettingRuleOp[] = PAGE_SETTING_RULE_OPS;
</script>

<template>
  <div
    v-for="(r, ri) in props.rules"
    :key="ri"
    class="flex flex-col gap-1 p-2 rounded-lg border border-line bg-subtle/40"
    data-testid="ps-rule"
  >
    <div class="flex gap-1 min-w-0">
      <select
        :value="r.field"
        :disabled="props.disabled"
        :aria-label="`${props.labelPrefix}regel ${ri + 1}: felt`"
        class="flex-1 min-w-0 px-2 py-1 rounded-lg border border-line bg-surface text-xs text-ink focus:outline-none focus:border-ink/40 disabled:opacity-50 disabled:cursor-not-allowed"
        @change="updateRule(ri, { field: selectValue($event) })"
      >
        <option v-for="o in props.ruleFields" :key="o.value" :value="o.value">{{ o.label }}</option>
      </select>
      <select
        :value="r.op"
        :disabled="props.disabled"
        :aria-label="`${props.labelPrefix}regel ${ri + 1}: operator`"
        class="w-[7.5rem] shrink-0 px-2 py-1 rounded-lg border border-line bg-surface text-xs text-ink focus:outline-none focus:border-ink/40 disabled:opacity-50 disabled:cursor-not-allowed"
        @change="updateRule(ri, { op: selectValue($event) as PageSettingRuleOp })"
      >
        <option v-for="op in RULE_OPS" :key="op" :value="op">{{ PAGE_SETTING_RULE_OP_LABELS[op] }}</option>
      </select>
    </div>
    <div class="flex gap-1 min-w-0">
      <input
        type="text"
        :value="r.value"
        :maxlength="PAGE_SETTING_RULE_VALUE_MAX"
        :disabled="props.disabled"
        :aria-label="`${props.labelPrefix}regel ${ri + 1}: værdi`"
        placeholder="Værdi"
        class="flex-1 min-w-0 px-2 py-1 rounded-lg border border-line bg-surface text-xs text-ink focus:outline-none focus:border-ink/40 disabled:opacity-50 disabled:cursor-not-allowed"
        @input="updateRule(ri, { value: inputValue($event) })"
      />
      <button
        v-if="!props.disabled"
        type="button"
        class="w-7 h-7 shrink-0 inline-flex items-center justify-center rounded-lg text-muted hover:bg-hover hover:text-ink transition-colors"
        :aria-label="`${props.labelPrefix}regel ${ri + 1}: fjern`"
        v-tip="'Fjern reglen'"
        @click="removeRule(ri)"
      >
        <span class="block w-3.5 h-3.5" v-html="ICONS.close" />
      </button>
    </div>
    <p v-if="!r.value.trim()" class="text-[11px] text-red">Skriv en værdi. En regel uden værdi gemmes ikke.</p>
  </div>
  <button
    v-if="!props.disabled"
    type="button"
    :disabled="props.rules.length >= PAGE_SETTING_RULES_MAX"
    class="self-start px-2.5 py-1 rounded-lg border border-line text-xs text-ink hover:bg-hover transition-colors disabled:opacity-40"
    data-testid="ps-rule-add"
    @click="addRule"
  >
    Tilføj regel
  </button>
</template>
